// The lineup health bar: the one place the site speaks up unasked.
//
// What it may do is flag a slot that *cannot score* — empty, on bye, Out — and
// open the choice. What it may not do is name the player to put there
// (docs/decisions/2026-09-12-tools-not-instructions.md), or put him there
// itself (docs/decisions/2026-10-10-the-site-never-sets-a-managers-lineup.md),
// so the button names the SLOT and the manager picks who fills it. It used to
// say "Start Pacheco" and "Pacheco is your best swap", and later carried an
// Optimize button; all three are gone, contract included.

import type { LineupHealth, LineupProblem } from '@fsp/domain';
import { useLeagueBase, useReaderZone } from '../../data/hooks';
import { useLeagueSource } from '../../data/leagueSource';
import { useLeagueNow } from '../../data/demo';
import { useSim } from '../../data/SimProvider';
import { lockNote } from './lock';
import { Icon } from '../../kit/Icon';
import { Button } from '../../kit/ui';
import { tourDone } from '../../demo/chapters';

/** "Pick a FLEX" for an empty slot, "Change QB" for one that cannot score. */
function actionLabel(problem: LineupProblem): string {
  return problem.code === 'empty-slot' ? `Pick a ${problem.rowSlot}` : `Change ${problem.rowSlot}`;
}

/** The fix's button (D9): a move, named by where it goes, never by who. */
function fixLabel(fix: NonNullable<LineupProblem['fix']>, reserveSlot: string | null): string {
  if (fix === 'to-reserve') return `Move to ${reserveSlot ?? 'reserve'}`;
  if (fix === 'off-reserve') return `Move off ${reserveSlot ?? 'reserve'}`;
  return 'Drop a player';
}

export function HealthBar({
  health,
  onOpen,
  onFix,
  reserveSlot = null,
  linkToLineup,
  locked,
}: {
  health: LineupHealth;
  /**
   * Make a problem's one move (`LineupProblem.fix`, D9), on a screen that
   * can: onto IR, off it to the bench, or a drop.
   */
  onFix?: (problem: LineupProblem) => void;
  /** The IR slot's name, for "Move to IR". */
  reserveSlot?: string | null;
  /** How many starters have locked, where the screen has the rows (B-221). */
  locked?: number;
  /** Open the slot for choosing, on a screen that can (the lineup). */
  onOpen?: (slot: string, slotIndex: number) => void;
  /** On a screen that cannot, link to the one that can, with the slot named. */
  linkToLineup?: boolean;
}) {
  const base = useLeagueBase();
  const zone = useReaderZone();
  // Read now: the league's in a real league — the wall's, or the demo league's
  // held Tuesday — and the simulator's in a practice one.
  const { now: simNow } = useSim();
  const leagueNow = useLeagueNow();
  const now = useLeagueSource() === 'api' ? leagueNow : simNow;
  // WHEN IT LOCKS, AT EVERY LEVEL (B-221): how many starters already have,
  // and when the next one does. A lineup that has wholly locked says so in
  // its headline instead.
  const locks = health.status === 'locked' ? null : lockNote(locked, health.locksAt, zone, now);
  const first = health.problems[0];
  const cls = `fsp-health fsp-health-${health.status}`;
  // The mark's color is the status's (`.fsp-health-mark[data-status]`).
  const icon = (
    <span className="fsp-health-mark" data-status={health.status}>
      <Icon
        name={health.status === 'ok' ? 'accept' : health.status === 'locked' ? 'locked' : 'warning'}
      />
    </span>
  );

  const openable = first?.swap && first.rowSlot !== null && first.rowSlotIndex !== null;
  // THE FIRST PROBLEM WITH A MOVE THAT CLEARS IT (D9), beside the slot's own
  // button when the first problem has both.
  const fixable = onFix ? health.problems.find((p) => p.fix !== null) : undefined;
  const fixButton =
    fixable && onFix ? (
      <Button size="xs" variant="caution" onClick={() => onFix(fixable)}>
        {fixLabel(fixable.fix!, reserveSlot)}
      </Button>
    ) : null;

  const action =
    openable && onOpen ? (
      <div className="fsp-health-actions">
        <Button
          size="xs"
          variant="caution"
          onClick={() => {
            onOpen(first!.rowSlot!, first!.rowSlotIndex!);
            tourDone('health-fix');
          }}
        >
          {actionLabel(first!)}
        </Button>
        {fixButton}
      </div>
    ) : fixButton !== null ? (
      fixButton
    ) : openable && linkToLineup ? (
      <Button
        to={`${base}?slot=${first!.rowSlot}&i=${first!.rowSlotIndex}`}
        size="xs"
        variant="caution"
      >
        {actionLabel(first!)}
      </Button>
    ) : linkToLineup ? (
      <Button to={base} size="xs" variant="quiet">
        Open
      </Button>
    ) : null;

  return (
    <div className={cls} role="status">
      {icon}
      <div className="fsp-health-body">
        <div className="fsp-health-headline">{health.headline}</div>
        {first && health.problems.length > 1 ? (
          // Every problem, wrapping. A one-line strip cut seven of them to
          // one and a half with an ellipsis nothing could open
          // (AUDIT-2026-09-23 U8).
          <div className="fsp-sub fsp-ui-wrap-text">
            {health.problems.map((p) => p.message).join(' ')}
          </div>
        ) : openable ? (
          // A count, not a name. It tells you there is a way out of this
          // without deciding it for you.
          <div className="fsp-sub fsp-ui-wrap-text">
            {first!.swap!.candidates} on your bench can play {first!.rowSlot}.
          </div>
        ) : first ? (
          // ONE PROBLEM WITH NO BENCH ANSWER says what it is: "The roster
          // needs a fix." alone does not say which, or why (D9).
          <div className="fsp-sub fsp-ui-wrap-text">{first.message}</div>
        ) : null}
        {locks !== null ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-health-locks">
            <Icon name="locked" size="sm" />
            {locks}
          </div>
        ) : null}
      </div>
      {action}
    </div>
  );
}
